import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeft, Mail, Phone, Building2, User, Clock,
  MessageSquarePlus, CheckCircle2, AlertCircle, CalendarPlus,
  Video, RefreshCw, CalendarCheck2, ChevronDown, ChevronUp,
  UserCheck, UserMinus,
} from 'lucide-react';
import { api } from './api';

const STATUS_LABELS = {
  nouveau: 'Nouveau',
  contacte: 'Contacté',
  rdv_planifie: 'RDV planifié',
  converti: 'Converti',
  archive: 'Archivé',
};
const STATUS_TONE = {
  nouveau: 'bg-sky-100 text-sky-700',
  contacte: 'bg-amber-100 text-amber-700',
  rdv_planifie: 'bg-violet-100 text-violet-700',
  converti: 'bg-emerald-100 text-emerald-700',
  archive: 'bg-slate-200 text-slate-600',
};
const REQUEST_STATUSES = ['nouveau', 'contacte', 'rdv_planifie', 'converti', 'archive'];

const APPT_STATUS_LABEL = {
  planifie: 'Planifié',
  confirme: 'Confirmé',
  realise: 'Réalisé',
  annule: 'Annulé',
};
const APPT_STATUS_TONE = {
  planifie: 'bg-violet-100 text-violet-700',
  confirme: 'bg-emerald-100 text-emerald-700',
  realise:  'bg-sky-100 text-sky-700',
  annule:   'bg-red-100 text-red-500',
};

const fmtApptDate = (iso) => {
  if (!iso) return '—';
  try {
    const s = iso.includes('T') ? iso : iso + 'T00:00:00';
    const d = new Date(s);
    return d.toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

function InfoBlock({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</span>
      <span className="flex items-center gap-1.5 text-sm text-slate-800 font-medium">
        {Icon && <Icon size={13} className="text-slate-400 flex-shrink-0" />}
        {value}
      </span>
    </div>
  );
}

/**
 * Affiche la fiche unifiée d'un prospect.
 * Props :
 *   - requests : tableau de demandes triées par date décroissante (la plus récente en [0])
 *   - admin, onBack, onSchedule, onStatusChange, onNoteSaved
 */
export default function RequestDetailPage({ requests: initialRequests, admin, onBack, onSchedule, onStatusChange, onNoteSaved }) {
  // Tableau local des demandes (mise à jour du statut en temps réel)
  const [requests, setRequests] = useState(initialRequests);
  const mainRequest = requests[0]; // Demande la plus récente

  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const notesEndRef = useRef(null);

  // Rendez-vous lié à la demande la plus récente
  const [appointment, setAppointment] = useState(undefined);
  const [loadingAppt, setLoadingAppt] = useState(true);

  // Historique des demandes (déplié automatiquement s'il y en a plusieurs)
  const [historyOpen, setHistoryOpen] = useState(initialRequests.length > 1);

  const fetchAppointment = useCallback(async () => {
    setLoadingAppt(true);
    try {
      const data = await api(`/demo-requests/${mainRequest.source}/${mainRequest.id}/appointment`);
      setAppointment(data.appointment ?? null);
    } catch { setAppointment(null); } finally { setLoadingAppt(false); }
  }, [mainRequest.source, mainRequest.id]);

  useEffect(() => { fetchAppointment(); }, [fetchAppointment]);

  const assignAppt = async () => {
    if (!appointment?.id) return;
    try {
      await api(`/appointments/${appointment.id}/assign`, { method: 'POST' });
      fetchAppointment();
    } catch (e) { alert(e.message); }
  };

  const unassignAppt = async () => {
    if (!appointment?.id) return;
    try {
      await api(`/appointments/${appointment.id}/assign`, { method: 'DELETE' });
      fetchAppointment();
    } catch (e) { alert(e.message); }
  };

  // Fusionner et trier toutes les notes de toutes les demandes par date croissante
  const allNotes = requests
    .flatMap((r) => (r.panel_notes_history || []))
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));

  useEffect(() => {
    if (allNotes.length > 0) {
      notesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allNotes.length]);

  const changeStatus = async (r, status) => {
    const prevStatus = r.statut;
    setRequests((prev) => prev.map((x) =>
      x.id === r.id && x.source === r.source ? { ...x, statut: status } : x
    ));
    try {
      await api(`/demo-requests/${r.source}/${r.id}`, { method: 'PATCH', body: { status } });
      onStatusChange?.(r.id, r.source, status);
    } catch {
      setRequests((prev) => prev.map((x) =>
        x.id === r.id && x.source === r.source ? { ...x, statut: prevStatus } : x
      ));
    }
  };

  const saveNote = async () => {
    const text = newNote.trim();
    if (!text) return;
    setSaving(true);
    setFeedback(null);
    try {
      const res = await api(`/demo-requests/${mainRequest.source}/${mainRequest.id}/notes`, {
        method: 'POST',
        body: { text },
      });
      // Mettre à jour la demande principale dans le tableau local
      setRequests((prev) => prev.map((r, i) =>
        i === 0 ? { ...r, panel_notes_history: [...(r.panel_notes_history || []), res.note] } : r
      ));
      onNoteSaved?.(mainRequest.id, mainRequest.source, res.note);
      setNewNote('');
      setFeedback({ type: 'ok', msg: 'Note enregistrée.' });
      setTimeout(() => setFeedback(null), 3000);
    } catch (e) {
      setFeedback({ type: 'err', msg: e.message || "Erreur lors de l'enregistrement." });
    } finally {
      setSaving(false);
    }
  };

  const adminLabel = admin?.name || admin?.email || 'Vous';

  return (
    <div data-testid="request-detail-page">

      {/* Breadcrumb retour */}
      <button
        onClick={onBack}
        data-testid="back-to-requests"
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-5 group transition-colors"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
        Retour aux demandes
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── Colonne gauche : fiche contact ── */}
        <div className="lg:col-span-1 space-y-4">

          {/* Identité */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-2 mb-4">
              <div>
                <h2 className="text-lg font-extrabold text-slate-900 leading-tight">{mainRequest.nom || '—'}</h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  {requests.length > 1
                    ? `${requests.length} demandes — dernière le ${fmtDate(mainRequest.created_at)}`
                    : fmtDate(mainRequest.created_at)
                  }
                </p>
              </div>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex-shrink-0 ${
                mainRequest.source === 'tunnel' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'
              }`}>
                {mainRequest.source === 'tunnel' ? 'Tunnel OTP' : 'Formulaire'}
              </span>
            </div>

            <div className="space-y-3">
              <InfoBlock label="E-mail"    value={mainRequest.email}     icon={Mail} />
              <InfoBlock label="Téléphone" value={mainRequest.telephone} icon={Phone} />
              <InfoBlock label="Société"   value={mainRequest.societe}   icon={Building2} />
              <InfoBlock label="Poste"     value={mainRequest.poste} />
              <InfoBlock label="Version"   value={mainRequest.version} />
            </div>
          </div>

          {/* Historique des demandes — visible uniquement si plusieurs demandes */}
          {requests.length > 1 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="requests-history-card">
              <button
                className="w-full flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                onClick={() => setHistoryOpen((o) => !o)}
                data-testid="requests-history-toggle"
              >
                <span>Historique des demandes ({requests.length})</span>
                {historyOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>

              {historyOpen && (
                <div className="space-y-3 mt-3">
                  {requests.map((r) => (
                    <div key={`${r.source}-${r.id}`} className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${r.source === 'tunnel' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'}`}>
                              {r.source === 'tunnel' ? 'Tunnel' : 'Formulaire'}
                            </span>
                            <p className="text-xs text-slate-400">{fmtDate(r.created_at)}</p>
                          </div>
                          {r.message && (
                            <p className="text-xs text-slate-500 italic line-clamp-2">« {r.message} »</p>
                          )}
                        </div>
                        <select
                          value={r.statut}
                          onChange={(e) => changeStatus(r, e.target.value)}
                          className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {REQUEST_STATUSES.map((s) => (
                            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Statut */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">
              Statut{requests.length > 1 ? ' (demande principale)' : ''}
            </p>
            <span className={`inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full mb-3 ${STATUS_TONE[mainRequest.statut] || 'bg-slate-100 text-slate-600'}`}>
              {STATUS_LABELS[mainRequest.statut] || mainRequest.statut}
            </span>
            <select
              value={mainRequest.statut}
              onChange={(e) => changeStatus(mainRequest, e.target.value)}
              data-testid="detail-status-select"
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 bg-slate-50 outline-none focus:border-slate-400"
            >
              {REQUEST_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>

          {/* Rendez-vous lié (demande la plus récente) */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="appt-card">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Rendez-vous</p>
              <button onClick={fetchAppointment} title="Actualiser" className="text-slate-300 hover:text-slate-600 transition-colors">
                <RefreshCw size={12} className={loadingAppt ? 'animate-spin' : ''} />
              </button>
            </div>

            {loadingAppt ? (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <RefreshCw size={12} className="animate-spin" /> Chargement…
              </div>
            ) : appointment ? (
              <div className="space-y-2.5" data-testid="appt-found">
                {/* Statut + badge attribution */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-block text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${APPT_STATUS_TONE[appointment.statut] || 'bg-slate-100 text-slate-600'}`}>
                    {APPT_STATUS_LABEL[appointment.statut] || appointment.statut}
                  </span>
                  {appointment.assigned_admin_name
                    ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 flex items-center gap-1">
                        <UserCheck size={11} /> {appointment.assigned_admin_name}
                      </span>
                    : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
                        Non attribué
                      </span>
                  }
                </div>

                {/* Date */}
                <div className="flex items-start gap-2">
                  <CalendarCheck2 size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-semibold text-slate-800 capitalize leading-snug">
                    {fmtApptDate(appointment.date_heure)}
                  </p>
                </div>

                {/* Boutons attribution */}
                {!appointment.assigned_admin_id && (
                  <button
                    onClick={assignAppt}
                    data-testid="detail-appt-assign"
                    className="flex items-center gap-1.5 w-full justify-center text-xs font-semibold text-indigo-700 border border-indigo-200 bg-indigo-50 rounded-xl px-3 py-2 hover:bg-indigo-100 transition-colors"
                  >
                    <UserCheck size={13} /> Me l'attribuer
                  </button>
                )}
                {appointment.assigned_admin_id === admin?.id && (
                  <button
                    onClick={unassignAppt}
                    data-testid="detail-appt-unassign"
                    className="flex items-center gap-1.5 w-full justify-center text-xs font-semibold text-slate-600 border border-slate-200 bg-slate-50 rounded-xl px-3 py-2 hover:bg-slate-100 transition-colors"
                  >
                    <UserMinus size={13} /> Se retirer
                  </button>
                )}

                {/* Lien visio */}
                {appointment.lien_visio && (
                  <a
                    href={appointment.lien_visio}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="appt-visio-btn"
                    className="flex items-center justify-center gap-2 w-full bg-[#0055a4] hover:bg-blue-700 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors mt-1"
                  >
                    <Video size={15} />
                    Rejoindre la réunion en ligne
                  </a>
                )}
                {appointment.notes && (
                  <p className="text-xs text-slate-500 italic border-t border-slate-100 pt-2 mt-1">
                    {appointment.notes}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2" data-testid="appt-none">
                <p className="text-xs text-slate-400">Aucun rendez-vous planifié.</p>
                <button
                  onClick={() => onSchedule({ nom: mainRequest.nom, email: mainRequest.email, request_ref: `${mainRequest.source}:${mainRequest.id}` })}
                  data-testid="appt-none-schedule-btn"
                  className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 underline underline-offset-2"
                >
                  <CalendarPlus size={12} /> Planifier maintenant
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Actions</p>
            <button
              onClick={() => onSchedule({ nom: mainRequest.nom, email: mainRequest.email, request_ref: `${mainRequest.source}:${mainRequest.id}` })}
              data-testid="detail-schedule-rdv"
              className="flex items-center justify-center gap-2 w-full text-sm font-semibold text-white bg-slate-900 hover:bg-slate-700 rounded-xl px-4 py-2.5 transition-colors"
            >
              <CalendarPlus size={15} /> Planifier un RDV
            </button>
          </div>

          {/* Message prospect (demande la plus récente) */}
          {mainRequest.message && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Message</p>
              <p className="text-sm text-slate-600 italic leading-relaxed">« {mainRequest.message} »</p>
            </div>
          )}
        </div>

        {/* ── Colonne droite : notes internes (fusionnées) ── */}
        <div className="lg:col-span-2 flex flex-col gap-4">

          {/* Timeline des notes */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 flex-1">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Notes internes
              </p>
              {allNotes.length > 0 && (
                <span className="bg-slate-100 text-slate-600 text-[11px] font-bold rounded-full px-2 py-0.5">
                  {allNotes.length}
                </span>
              )}
            </div>

            {allNotes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <MessageSquarePlus size={28} className="text-slate-200 mb-2" />
                <p className="text-sm text-slate-400">Aucune note pour l'instant.</p>
                <p className="text-xs text-slate-300 mt-1">Ajoutez-en une ci-dessous.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {allNotes.map((n, i) => (
                  <div key={i} className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
                    <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{n.text}</p>
                    <div className="flex items-center gap-3 mt-2.5">
                      <span className="flex items-center gap-1 text-[11px] text-slate-500">
                        <User size={11} className="text-slate-400" />
                        <span className="font-semibold">{n.admin_name}</span>
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-slate-400">
                        <Clock size={11} />
                        {fmtDate(n.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
                <div ref={notesEndRef} />
              </div>
            )}
          </div>

          {/* Saisie nouvelle note */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">
              Ajouter une note
            </p>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Saisissez votre note ici… (Ctrl+Entrée pour enregistrer)"
              rows={4}
              data-testid="note-textarea"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveNote();
              }}
              className="w-full text-sm border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 resize-none bg-slate-50 transition-all"
            />
            <div className="flex items-center justify-between gap-3 mt-3">
              <div className="flex items-center gap-2 text-xs text-slate-400 min-w-0">
                {feedback ? (
                  feedback.type === 'ok' ? (
                    <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                      <CheckCircle2 size={13} /> {feedback.msg}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-600 font-semibold">
                      <AlertCircle size={13} /> {feedback.msg}
                    </span>
                  )
                ) : (
                  <span className="flex items-center gap-1 truncate">
                    <User size={11} className="flex-shrink-0" />
                    Sous <strong className="ml-1">{adminLabel}</strong>
                  </span>
                )}
              </div>
              <button
                onClick={saveNote}
                disabled={saving || !newNote.trim()}
                data-testid="save-note-btn"
                className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2 rounded-xl transition-colors flex-shrink-0"
              >
                <MessageSquarePlus size={14} />
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
